import {Dedupe as DedupeIntegration} from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import {BrowserTracing} from '@sentry/tracing';

import {deploymentConfig} from './applicationConfig';
import {logger, loggingAvailable, sentryDiagnostics} from './logger';

// TODO: Find a better way to handle this?
declare const process: {env: Record<string, string>};

const SENTRY_REPORT_MIN_DELAY = 30 * 1000;
const SENTRY_LAST_LOG_KEY = 'infinity-connect:lastError:ts';

function hasEventTarget(
    hint: {event?: {target?: unknown}} | undefined,
): hint is {
    event: {target: unknown};
} {
    return !!hint?.event?.target;
}

/**
 * A breadcrumb without the URL fields of its data, such as the `url` of a
 * fetch or the `from` and `to` of a navigation. A URL can carry a conference
 * alias, a token or a query, so it is omitted; the method and status remain.
 *
 * @example
 * withoutBreadcrumbUrls({category: 'fetch', data: {method: 'GET', url: 'https://x/?pin=1', status_code: 200}});
 * // {category: 'fetch', data: {method: 'GET', status_code: 200}}
 */
const withoutBreadcrumbUrls = (
    breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb => {
    if (!breadcrumb.data) {
        return breadcrumb;
    }
    const {url: _url, from: _from, to: _to, ...data} = breadcrumb.data;
    return {...breadcrumb, data};
};

/**
 * An event without the URL fields of its request, which hold the page URL,
 * its query and the referrer, and with every breadcrumb's URL fields omitted.
 *
 * @example
 * withoutUrls({request: {url: 'https://app/m/alias', headers: {Referer: 'x', 'User-Agent': 'y'}}});
 * // {request: {headers: {'User-Agent': 'y'}}}
 */
const withoutUrls = <T extends Sentry.Event>(event: T): T => {
    const {request, breadcrumbs} = event;
    let sanitizedRequest: Sentry.Event['request'];
    if (request) {
        const {
            url: _url,
            query_string: _query,
            cookies: _cookies,
            headers,
            ...rest
        } = request;
        const {Referer: _referer, ...otherHeaders} = headers ?? {};
        sanitizedRequest = {
            ...rest,
            ...(headers ? {headers: otherHeaders} : {}),
        };
    }
    return {
        ...event,
        ...(sanitizedRequest ? {request: sanitizedRequest} : {}),
        ...(breadcrumbs
            ? {breadcrumbs: breadcrumbs.map(withoutBreadcrumbUrls)}
            : {}),
    };
};

/**
 * A span as Sentry sends it. A transaction's spans are still `Span` instances
 * in `beforeSendTransaction`; they refer back to their transaction, and only
 * their `toJSON` gives the wire format, so they are serialized before the
 * event is copied.
 *
 * @example
 * serializeSpan(span); // {span_id: '...', op: 'http.client', ...}
 */
const serializeSpan = (span: unknown): unknown => {
    if (typeof span !== 'object' || span === null || !('toJSON' in span)) {
        return span;
    }
    const {toJSON} = span;
    return typeof toJSON === 'function' ? toJSON.call(span) : span;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A serialized span without its URL: the `url` of its data is omitted, and an
 * HTTP client span, whose description is `<method> <url>`, is described by
 * its method only.
 *
 * @example
 * withoutSpanUrls({op: 'http.client', description: 'GET https://x/a', data: {url: 'https://x/a', 'http.method': 'GET'}});
 * // {op: 'http.client', description: 'GET', data: {'http.method': 'GET'}}
 */
const withoutSpanUrls = (span: unknown): unknown => {
    if (!isRecord(span)) {
        return span;
    }
    const {data, ...rest} = span;
    const {url: _url, ...safeData} = isRecord(data) ? data : {};
    const method = safeData['http.method'];
    const isHttpClient =
        typeof rest.op === 'string' && rest.op.startsWith('http.');
    return {
        ...rest,
        ...(isHttpClient
            ? {description: typeof method === 'string' ? method : undefined}
            : {}),
        ...(isRecord(data) ? {data: safeData} : {}),
    };
};

/**
 * The item Sentry may send: a redacted copy without URL fields and with
 * serialized spans, or `null` to drop it when it cannot be redacted or while
 * redaction is incomplete.
 *
 * @example
 * beforeSend: event => toTransportable(event)
 */
const toTransportable = <T extends Sentry.Event>(event: T): T | null => {
    const sanitized = withoutUrls(event);
    const serializable = sanitized.spans
        ? {
              ...sanitized,
              spans: sanitized.spans.map(span =>
                  withoutSpanUrls(serializeSpan(span)),
              ),
          }
        : sanitized;
    // A redacted JSON copy keeps the shape of the item it copies.
    return (sentryDiagnostics.redact(serializable) as T | undefined) ?? null;
};

/**
 * The Sentry fetch transport, sending nothing while redaction is incomplete.
 * Items are checked again when they are dispatched, so an item that passed
 * `beforeSend` before redaction became incomplete is still discarded.
 *
 * @example
 * Sentry.init({transport: createGuardedTransport});
 */
const createGuardedTransport = (
    options: Parameters<typeof Sentry.makeFetchTransport>[0],
): ReturnType<typeof Sentry.makeFetchTransport> => {
    const transport = Sentry.makeFetchTransport(options);
    return {
        send: envelope =>
            sentryDiagnostics.isRedactionIncomplete()
                ? Promise.resolve({})
                : transport.send(envelope),
        flush: timeout => transport.flush(timeout),
    };
};

export function setupSentry() {
    if (!loggingAvailable || !deploymentConfig.sentry) {
        return;
    }
    Sentry.init({
        integrations: [new BrowserTracing(), new DedupeIntegration()],
        release: process.env.BUILD_HASH_FULL,
        ...deploymentConfig.sentry,

        // Set after the deployment configuration, so it cannot replace them.
        transport: createGuardedTransport,
        beforeBreadcrumb(breadCrumb, hint) {
            if (hasEventTarget(hint)) {
                const target = hint.event.target;
                if (target instanceof HTMLElement && target.dataset.testid) {
                    breadCrumb.message = target.dataset.testid;
                }
            }
            const sanitized = sentryDiagnostics.redact(
                withoutBreadcrumbUrls(breadCrumb),
            );
            // A redacted JSON copy keeps the shape of the breadcrumb.
            return (sanitized as Sentry.Breadcrumb | undefined) ?? null;
        },
        beforeSend: event => toTransportable(event),
        beforeSendTransaction: event => toTransportable(event),
    });

    Sentry.addGlobalEventProcessor(event => {
        if (event.type !== 'transaction') {
            // Filter out ResizeObserver errors
            // FIXME: #2525
            if (event.message?.includes('ResizeObserver')) {
                return null;
            }
            const lastError = Number.parseInt(
                localStorage.getItem(SENTRY_LAST_LOG_KEY) ?? '0',
                10,
            );
            if (lastError + SENTRY_REPORT_MIN_DELAY > Date.now()) {
                logger.warn({lastError}, 'Event has been throttled');
                return null;
            }
            localStorage.setItem(SENTRY_LAST_LOG_KEY, String(Date.now()));
        }

        return event;
    });
}
